import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AuthModalOverlay } from '@/components/auth/AuthShell';
import { useUserStore } from '@/store/useUserStore';
import { api } from '@/lib/api';
import { getFirstAccessiblePath } from '@/lib/permission';

const REDIRECT_SECONDS = 10;

/**
 * SessionActiveModal
 *
 * Shown on the public auth pages (login, register, password flows) when the
 * user still has an active session. Rather than silently rendering a login form
 * to someone who is already signed in, we surface the session and let them
 * either continue into the app (auto-continues after a short countdown) or log
 * out to sign in as a different user.
 *
 * The component owns its own visibility: once the user logs out it hides itself
 * (instead of re-deriving from the store), so a subsequent form login on the
 * same page can't flash the modal again.
 */
export const SessionActiveModal = () => {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const logout = useUserStore((state) => state.logout);
  const clearCache = useUserStore((state) => state.clearCache);

  const [open, setOpen] = useState(true);
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  // Guards against the countdown firing after the user has already acted.
  const settledRef = useRef(false);

  const email = user?.user?.email ?? '';

  const continueToApp = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    const state = useUserStore.getState();
    const target = getFirstAccessiblePath(state.role, state.user?.permissions ?? []);
    navigate(target, { replace: true });
  };

  const handleLogout = () => {
    settledRef.current = true;
    logout();
    clearCache();
    api.clearCache();
    // Stay on the current auth page so the form is usable for the next sign-in.
    setOpen(false);
  };

  useEffect(() => {
    const id = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          continueToApp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) return null;

  return (
    <AuthModalOverlay>
      <div className="flex w-full max-w-sm flex-col items-center gap-5 rounded-xl bg-white p-6 text-center shadow-xl sm:p-8">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="1.5" />
            <path d="M12 8v4M12 16h.01" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>

        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-zinc-900">Session still active</h2>
          <p className="text-sm leading-relaxed text-zinc-500">
            You're still signed in{email ? (
              <> as <strong>{email}</strong></>
            ) : null}. Continue to your dashboard, or log out to sign in with a
            different account.
          </p>
          <p className="text-xs text-zinc-400">
            Redirecting to your dashboard in {seconds}s&hellip;
          </p>
        </div>

        <Button
          onClick={continueToApp}
          className="h-11 w-full bg-brand-700 font-semibold text-white transition-colors hover:bg-brand-800"
        >
          Continue to my dashboard
        </Button>
        <button
          onClick={handleLogout}
          className="cursor-pointer text-sm text-zinc-500 underline hover:text-brand-700"
        >
          Log out &amp; sign in as a different user
        </button>
      </div>
    </AuthModalOverlay>
  );
};
